import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, ErrorText, Input, Label, Select, Textarea } from "@/components/ui";
import { api } from "@/lib/api";

interface Endpoint {
  id: string;
  name: string;
}

interface EmbeddingConfig {
  endpoint_id: string | null;
  model: string;
  query_prefix: string;
}

export default function EmbeddingsAdmin() {
  const cfg = useQuery({ queryKey: ["admin", "embeddings"], queryFn: () => api.get<EmbeddingConfig>("/api/admin/embeddings") });
  const endpoints = useQuery({ queryKey: ["admin", "endpoints"], queryFn: () => api.get<Endpoint[]>("/api/admin/endpoints") }).data ?? [];
  const [form, setForm] = useState<EmbeddingConfig | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (cfg.data && !form) setForm(cfg.data);
  }, [cfg.data, form]);
  const save = useMutation({ mutationFn: () => api.put("/api/admin/embeddings", form), onSuccess: () => setSaved(true) });
  const test = useMutation({ mutationFn: () => api.post<{ dimensions: number }>("/api/admin/embeddings/test", form) });
  if (!form) return null;
  const set = (patch: Partial<EmbeddingConfig>) => {
    setSaved(false);
    test.reset();
    setForm({ ...form, ...patch });
  };
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card title="Search by meaning">
        <p className="mb-3 text-sm text-zinc-500">
          An OpenAI-compatible /embeddings endpoint (e.g. Qwen3-Embedding through llama.cpp). With it, models in a space can search records and files by meaning. The
          endpoint's models need not be enabled for chat. Changing the model indexes every space again, in the background.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Endpoint">
            <Select value={form.endpoint_id ?? ""} onChange={(e) => set({ endpoint_id: e.target.value || null })}>
              <option value="">Off</option>
              {endpoints.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Select>
          </Label>
          <Label label="Model">
            <Input value={form.model} onChange={(e) => set({ model: e.target.value })} placeholder="embedding" />
          </Label>
        </div>
        <div className="mt-4">
          <Label label="Query prefix" hint="Put before search queries, not before documents. Qwen3-Embedding wants an instruction; leave empty for other models.">
            <Textarea rows={2} value={form.query_prefix} onChange={(e) => set({ query_prefix: e.target.value })} />
          </Label>
        </div>
      </Card>
      <ErrorText error={save.error || test.error} />
      <div className="flex items-center gap-3">
        <Button disabled={save.isPending}>Save</Button>
        <Button type="button" variant="secondary" disabled={!form.endpoint_id || !form.model || test.isPending} onClick={() => test.mutate()}>
          Test
        </Button>
        {test.data && <span className="text-sm text-green-600">Works: {test.data.dimensions} dimensions.</span>}
        {saved && <span className="text-sm text-green-600">Saved.</span>}
      </div>
    </form>
  );
}
